import React,{useEffect,useState} from 'react';
import { Routes,Route,Navigate } from 'react-router-dom';
import Layout from './components/Layout';
import PublicLayout from './layouts/PublicLayout';
import Dashboard from './pages/Dashboard';
import Documents from './pages/Documents';
import DocumentEditor from './pages/DocumentEditor';
import VersionCompare from './pages/VersionCompare';
import Templates from './pages/Templates';
import PublicLinks from './pages/PublicLinks';
import Exports from './pages/Exports';
import Settings from './pages/Settings';
import HelpSupport from './pages/HelpSupport';
import Login from './pages/Login';
import PublicHub from './pages/public/PublicHub';
import PublicDocumentViewer from './pages/public/PublicDocumentViewer';
import {legalVaultService} from './services/legalVaultService';

async function reconcileMissingPublishedLinks() {
  if (!legalVaultService.hasSettingsAccess()) return;
  try {
    const client=await legalVaultService.requirePocketBase('reconcileMissingPublishedLinks');
    const [documents,links]=await Promise.all([
      client.collection('legal_documents').getFullList({
        filter:'status="published" && (visibility="public" || visibility="unlisted")',
      }),
      client.collection('legal_public_links').getFullList({}),
    ]);
    const now=new Date().toISOString();
    for (const document of documents) {
      const documentLinks=links.filter((link)=> link.documentId===document.id);
      const usableActiveLinks=documentLinks.filter((link)=> link.isActive===true && (!link.expiresAt || link.expiresAt>now));
      const hasCanonicalActiveLink=usableActiveLinks.some((link)=> link.slug===document.slug);
      if (hasCanonicalActiveLink) continue;
      const hasOnlyDisabledOrExpiredLinks=documentLinks.length>0 && usableActiveLinks.length===0;
      if (hasOnlyDisabledOrExpiredLinks) continue;
      await legalVaultService.ensurePublicLinkForDocument(document.id);
    }
  } catch (error) {
    console.warn('Public link reconciliation skipped:',error);
  }
}

function ProtectedRoute({children}) {
  const isAuthenticated=legalVaultService.isAuthenticated();
  const hasViewerAccess=legalVaultService.hasViewerAccess();
  return isAuthenticated && hasViewerAccess ? children : <Navigate to="/login" replace />;
}

function EditorRoute({children}) {
  const isAuthenticated=legalVaultService.isAuthenticated();
  const canEditDocuments=legalVaultService.hasAdminAccess();
  return isAuthenticated && canEditDocuments ? children : <Navigate to="/" replace />;
}

function AdminRoute({children}) {
  const isAuthenticated=legalVaultService.isAuthenticated();
  const hasSettingsAccess=legalVaultService.hasSettingsAccess();
  return isAuthenticated && hasSettingsAccess ? children : <Navigate to="/login" replace />;
}

export default function App() {
  const [isInitializing,setIsInitializing]=useState(true);

  useEffect(()=> {
    legalVaultService.initialisePocketBase()
      .then(()=> reconcileMissingPublishedLinks())
      .catch(err=> console.error('App init error:',err))
      .finally(()=> setIsInitializing(false));
  },[]);

  if (isInitializing) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center text-slate-500 flex flex-col items-center">
          <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mb-4"></div>
          <p>Connecting to secure vault...</p>
        </div>
      </div>
    );
  }

  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/legal" element={<PublicLayout />}>
        <Route index element={<PublicHub />} />
        <Route path="version/:versionId" element={<PublicDocumentViewer />} />
        <Route path=":slug" element={<PublicDocumentViewer />} />
        <Route path=":slug/:versionId" element={<PublicDocumentViewer />} />
      </Route>
      <Route path="/" element={<ProtectedRoute><Layout /></ProtectedRoute>}>
        <Route index element={<Dashboard />} />
        <Route path="documents" element={<Documents />} />
        <Route path="documents/:documentId" element={<DocumentEditor />} />
        <Route path="documents/:documentId/compare" element={<VersionCompare />} />
        <Route path="documents/:documentId/edit" element={<EditorRoute><DocumentEditor /></EditorRoute>} />
        <Route path="documents/:documentId/versions/:versionId/edit" element={<EditorRoute><DocumentEditor /></EditorRoute>} />
        <Route path="templates" element={<EditorRoute><Templates /></EditorRoute>} />
        <Route path="public-links" element={<AdminRoute><PublicLinks /></AdminRoute>} />
        <Route path="exports" element={<AdminRoute><Exports /></AdminRoute>} />
        <Route path="settings" element={<AdminRoute><Settings /></AdminRoute>} />
        <Route path="help" element={<HelpSupport />} />
      </Route>
      <Route path="*" element={<Navigate to="/login" replace />} />
    </Routes>
  );
}